import React from 'react';
import { chartColors, themeColors } from '../../../constants';
import {
  makeStyles,
  Theme,
  createStyles,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IChartProps, IReduxState } from '../../../types';
import { CardLayout } from '../CardLayouts/';
import { FormatHelper } from '../../../helpers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    lastUpdate: {
      position: 'absolute',
      left: theme.spacing(1),
      top: 0,
      color: themeColors.white,
    },
    gaugeWrapper: {
      height: 175,
      width: 200,
    },
    firstEntry: {
      margin: `${theme.spacing(2)}px 0 0 0`,
      [theme.breakpoints.up('md')]: {
        margin: 0,
      },
      [theme.breakpoints.up('lg')]: {
        margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px 0`,
      },
    },
    title: {
      marginTop: theme.spacing(2),
    },
    displayItem: {
      margin: theme.spacing(1),
    },
    display: {
      margin: theme.spacing(1),
    },
    valuesWrapper: {
      display: 'flex',
      justifyContent: 'center',
    },
  })
);

export default function FuelConsumptionDisplayHybrid(props: IChartProps) {
  const { isPdfReport } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const formatHelper = new FormatHelper(t);
  
  const isHybrid = useSelector(
    (state: IReduxState) =>
      // !!state.machineDetails.machine.telemetrySnapshot?.isHybrid
      !!state.machineDetails.machine.isHybrid
  );

  const { 
    averageFuelConsumption,
    averageEnergyConsumption,
    co2Emission,
  } = useSelector(
    (state: IReduxState) => state.machineDetails
  );

  const {
    engineFuelRate,
    engineFuelConsumed,
    mainDischargeConveyorFuelRate,
    sideDischargeConveyorFuelRate,
  } = averageFuelConsumption;
  const {
    mainDischargeConveyorEnergyRate,
    sideDischargeConveyorEnergyRate,
    energyConsumeRate,
    totalEnergyConsumed,
  } = averageEnergyConsumption;

  // console.log("***************************************");
  // console.log("In FuelConsumptionDisplayHybrid!! ");
  // console.log("***************************************");

  return (
    <CardLayout
      cardStyle={{
        boxShadow:
          isHybrid && !isPdfReport
            ? `0.25rem 0.25rem 0 0 ${chartColors.electricBlue}`
            : 'none',
      }}
    >
      <div>
        <Typography className={classes.lastUpdate} variant="caption">
          {`${t('graph.lastReceivedValue')}: ${FormatHelper.formatTimestamp(averageFuelConsumption.timestamp)}`}
        </Typography>

        <Typography
          align="center"
          variant="h5"
          className={classes.title}
        >
          {t('graph.averageEnergyConsumption')}
        </Typography>

        <div className={classes.firstEntry}>
          <Typography align="center" variant="body1">
            {t('graph.dieselEngine')} / {t('graph.electricDrive')}
          </Typography>

          <div className={classes.valuesWrapper}>
            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.purple }}
            >
              {formatHelper.formatUnitValueExceptZero(engineFuelRate)}
            </Typography>

            <Typography
              align="center"
              variant="body1"
              style={{ margin: '0 0.5rem' }}
            >
              /
            </Typography>

            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.electricBlue }}
            >
              {formatHelper.formatUnitValueExceptZero(energyConsumeRate)}
            </Typography>
          </div>
        </div>

        <div className={classes.display}>
          <Typography align="center" variant="body1">
            {t('graph.mainDischargeConveyor')}
          </Typography>

          <div className={classes.valuesWrapper}>
            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.cyan }}
            >
              {formatHelper.formatUnitValueExceptZero(mainDischargeConveyorFuelRate)}
            </Typography>

            <Typography
              align="center"
              variant="body1"
              style={{ margin: '0 0.5rem' }}
            >
              /
            </Typography>

            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.cyan }}
            >
              {formatHelper.formatUnitValueExceptZero(mainDischargeConveyorEnergyRate)}
            </Typography>
          </div>
        </div>

        <div className={classes.display}>
          <Typography align="center" variant="body1">
            {t('graph.secondDischargeConveyor')}
          </Typography>

          <div className={classes.valuesWrapper}>
            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.green }}
            >
              {formatHelper.formatUnitValueExceptZero(sideDischargeConveyorFuelRate)}
            </Typography>

            <Typography
              align="center"
              variant="body1"
              style={{ margin: '0 0.5rem' }}
            >
              /
            </Typography>

            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.green }}
            >
              {formatHelper.formatUnitValue(sideDischargeConveyorEnergyRate)}
            </Typography>
          </div>
        </div>

        <div className={classes.display}>
          <Typography align="center" variant="body1">
            {t('graph.energyConsumed')}
          </Typography>

          <div className={classes.valuesWrapper}>
            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.purple }}
            >
              {formatHelper.formatUnitValue(engineFuelConsumed)}
            </Typography>

            <Typography
              align="center"
              variant="body1"
              style={{ margin: '0 0.5rem' }}
            >
              /
            </Typography>

            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.electricBlue }}
            >
              {formatHelper.formatUnitValue(totalEnergyConsumed)}
            </Typography>
          </div>
        </div>
        
        <div className={classes.display}>
          <Typography
            align="center"
            variant="h5"
            className={classes.title}
          >
              CO<sub>2</sub>{t('graph.co2Emissions').replace("CO2", "")}
          </Typography>

          <div className={classes.valuesWrapper}>
            <Typography
              align="center"
              variant="body1"
              style={{ color: chartColors.co2EmissionGreen }}
            >
              {co2Emission !== null ? (
                <span>
                  {`${co2Emission.value}  ${co2Emission.unit}`} CO<sub>2</sub>
                </span>
              ) : (
                t('graph.noData')
              )}
            </Typography>
          </div>
        </div>
      </div>
    </CardLayout>
  );
}
